import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "@/lib/api";
import { inr, num, MONTHS } from "@/lib/format";
import { KpiCard, PageLoader, CHART_COLORS } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { Users, Library, BookCopy, IndianRupee, Wallet, LifeBuoy, Printer } from "lucide-react";

export default function AdminDashboard() {
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-overview", year, month],
    queryFn: async () => {
      const params = {};
      if (year !== "all") params.year = year;
      if (month !== "all") params.month = month;
      return (await api.get("/admin/overview", { params })).data;
    },
  });

  if (isLoading || !data) return <PageLoader />;

  const pie = data.platform_split.filter((p) => p.profit > 0);
  const filtering = year !== "all" || month !== "all";

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Publisher overview</p>
          <p className="text-sm text-muted-foreground mt-1">A snapshot of authors, books and royalties across Manda Publishers.</p>
        </div>
        <div className="flex gap-3">
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Year</p>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-[120px] h-9" data-testid="admin-filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {data.available_years?.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Month</p>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[130px] h-9" data-testid="admin-filter-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <KpiCard testid="kpi-total-profit" label={filtering ? "Royalty Profit (filtered)" : "Total Royalty Profit"} value={inr(data.total_profit)} icon={IndianRupee} accent sub="Across all authors" delay={0} />
        <KpiCard testid="kpi-authors" label="Authors" value={num(data.authors)} icon={Users} delay={60} />
        <KpiCard testid="kpi-books" label="Published Books" value={num(data.books)} icon={Library} delay={120} />
        <KpiCard testid="kpi-copies" label={filtering ? "Copies Sold (filtered)" : "Copies Sold"} value={num(data.total_copies)} icon={BookCopy} delay={180} />
        <KpiCard testid="kpi-pending-redemptions" label="Pending Redemptions" value={num(data.pending_redemptions)} icon={Wallet} delay={240} />
        <KpiCard testid="kpi-pending-orders" label="Pending Reprints" value={num(data.pending_orders)} icon={Printer} delay={300} />
        <KpiCard testid="kpi-open-tickets" label="Open Tickets" value={num(data.open_tickets)} icon={LifeBuoy} delay={360} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 lg:p-8">
          <h3 className="font-head text-lg font-medium">Profit by Platform</h3>
          <p className="text-xs text-muted-foreground mb-4">Royalty profit share across sales channels</p>
          {pie.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No sales recorded yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pie} dataKey="profit" nameKey="platform" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}>
                  {pie.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => inr(v)}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--popover-foreground))" }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6 lg:p-8">
          <h3 className="font-head text-lg font-medium">Copies by Platform</h3>
          <p className="text-xs text-muted-foreground mb-4">Units sold on each channel</p>
          {data.platform_split.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No sales recorded yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.platform_split} margin={{ left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
                <XAxis dataKey="platform" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                  formatter={(v) => num(v)}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--popover-foreground))" }}
                />
                <Bar dataKey="copies" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} maxBarSize={64} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
